import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Menu, X, Quote, Users, Camera, Tag, Star } from "lucide-react";
import { toast } from "sonner";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/be6dff42-6e9d-42fb-b6c8-aeada485811a/images/32c5ba27e60613e58e2287c0b81b4b3ac3d1234bf16da08c99af6cdd858f9ec9.png",
  brickTexture:
    "https://static.prod-images.emergentagent.com/jobs/be6dff42-6e9d-42fb-b6c8-aeada485811a/images/9abc4a3f078a4a07790e701d5e8da26d5332dd3e3c61963ba3acd5330b8a95b4.png",
  case1:
    "https://static.prod-images.emergentagent.com/jobs/be6dff42-6e9d-42fb-b6c8-aeada485811a/images/b7156593e29483b15342606697fba202712f1a40c284230a42b8b9044a383b0f.png",
  case2:
    "https://images.unsplash.com/photo-1586251695348-8d5d421ea794?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxicmljayUyMGFyY2hpdGVjdHVyZSUyMHBhcmlzfGVufDB8fHx8MTc3ODIyODI0NHww&ixlib=rb-4.1.0&q=85",
  case3:
    "https://images.pexels.com/photos/14705335/pexels-photo-14705335.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  client:
    "https://images.unsplash.com/photo-1731341711390-a721b4e31b6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDQ2NDF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGNsaWVudCUyMHBvcnRyYWl0JTIwaGVhZHNob3R8ZW58MHx8fHwxNzc4MjI4MjY0fDA&ixlib=rb-4.1.0&q=85",
  founder:
    "https://customer-assets.emergentagent.com/job_vendez-petit-paris/artifacts/o5ubdtdf_immolynk-a-propos.jpg",
  testimonial:
    "https://customer-assets.emergentagent.com/job_vendez-petit-paris/artifacts/3hkqgxhl_Gre%CC%81goire.png",
};

const CONTACT = {
  phone: "06 68 70 01 21",
  phoneTel: "+33668700121",
  email: "pierre@lexpertparisien.fr",
  brand: "L'Expert Parisien",
  domain: "lexpertparisien.fr",
  expert: "Pierre Travers",
  partner: "Flatlink",
  partnerUrl: "https://www.flatlink.fr",
  partnerReviewsUrl: "https://www.google.com/search?q=Flatlink+avis",
  address: "Paris & Proche couronne",
};

// ---------- HEADER ----------
const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#expertise", label: "Expertise" },
    { href: "#a-propos", label: "À propos" },
    { href: "#realisations", label: "Réalisations" },
    { href: "#honoraires", label: "Honoraires" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F8F6]/85 backdrop-blur-md border-b border-[#E5E0D8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid="brand-logo"
          className="font-serif text-2xl md:text-[26px] tracking-tight text-[#1E1F1C]"
        >
          {CONTACT.brand}
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-sans text-sm tracking-wide text-[#1E1F1C] hover:text-[#B85C38] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          data-testid="header-cta-button"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#1E1F1C] text-white font-sans text-xs tracking-[0.15em] uppercase hover:bg-[#B85C38] transition-colors"
        >
          Estimer mon bien
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </a>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-[#1E1F1C]"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#F9F8F6] border-t border-[#E5E0D8]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-sans text-base text-[#1E1F1C]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1E1F1C] text-white font-sans text-xs tracking-[0.15em] uppercase"
            >
              Estimer mon bien
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ---------- HERO ----------
const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative pt-36 pb-20 md:pt-44 md:pb-32 px-6 md:px-10 lg:px-12 bg-[#F9F8F6]"
  >
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
      <div className="md:col-span-7 fade-up">
        <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-6">
          Studios & petites surfaces — Paris
        </p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight text-[#1E1F1C] leading-[1.05]">
          Vendez votre <em className="italic font-light">petite surface</em>
          <br />
          au meilleur prix.
        </h1>
        <p className="mt-8 text-lg md:text-xl font-sans text-[#5C5D58] leading-relaxed max-w-xl">
          Spécialiste des studios et petites surfaces à Paris & proche couronne.
          Accédez à des acquéreurs ciblés pour vendre rapidement et dans les
          meilleures conditions.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a
            href="#contact"
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#B85C38] text-white font-sans text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#9A4C2E] transition-colors"
          >
            Estimer mon bien
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <a
            href="#realisations"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#1E1F1C] text-[#1E1F1C] font-sans text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#1E1F1C] hover:text-white transition-colors"
          >
            Voir les ventes récentes
          </a>
        </div>

        <div className="mt-14 grid grid-cols-3 gap-0 max-w-2xl border-t border-[#E5E0D8] pt-8">
          {[
            {
              icon: Users,
              value: "500+",
              label: "Investisseurs actifs",
              desc: "Un réseau qualifié prêt à acheter.",
            },
            {
              icon: Camera,
              value: "10+",
              label: "Photos Pro offertes",
              desc: "Un pack pro pour sublimer votre bien sans frais.",
            },
            {
              icon: Tag,
              value: "3,5%",
              label: "Honoraires max",
              desc: "Des honoraires justes, sans mauvaises surprises.",
            },
          ].map((s, i, arr) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className={`flex flex-col px-4 md:px-5 ${
                  i !== arr.length - 1 ? "border-r border-[#E5E0D8]" : ""
                } ${i === 0 ? "pl-0" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#F0EBE1] flex items-center justify-center mt-1">
                    <Icon className="w-4 h-4 text-[#1E1F1C]" strokeWidth={1.5} />
                  </span>
                  <div className="font-serif text-[26px] md:text-[30px] text-[#B85C38] leading-[1.05] tracking-tight">
                    {s.value}
                  </div>
                </div>
                <div className="font-sans text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#5C5D58] mt-3 font-medium">
                  {s.label}
                </div>
                <p className="font-sans text-xs md:text-sm text-[#5C5D58] mt-3 leading-relaxed max-w-[180px]">
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="md:col-span-5 fade-up delay-200">
        <div className="img-frame relative aspect-[3/4] w-full">
          <img
            src={IMG.hero}
            alt="Façade d'un immeuble parisien en briques des années 30"
            className="w-full h-full object-cover"
            data-testid="hero-image"
          />
          <div className="absolute bottom-6 left-6 right-6 bg-[#F9F8F6]/95 backdrop-blur-sm px-5 py-4 border border-[#E5E0D8]">
            <div className="font-serif text-base text-[#1E1F1C]">
              Paris 11ᵉ — Studio 18 m²
            </div>
            <div className="font-sans text-xs text-[#5C5D58] mt-1">
              Vendu 215 000 €
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- TRUST / PROOF ----------
const Trust = () => {
  const benefits = [
    {
      n: "01",
      t: "Réseau d'acquéreurs ciblés",
      d: "Une base qualifiée d'investisseurs et primo-accédants spécifiquement intéressés par les petites surfaces parisiennes.",
    },
    {
      n: "02",
      t: "Stratégie de prix optimisée",
      d: "Une analyse fine du marché studio et 2 pièces, arrondissement par arrondissement, pour positionner votre bien au juste prix.",
    },
    {
      n: "03",
      t: "Vente rapide et maîtrisée",
      d: "Un accompagnement complet, de la mise en valeur du bien jusqu'à la signature, sans perte de temps ni concession inutile.",
    },
  ];

  return (
    <section
      id="expertise"
      data-testid="trust-section"
      className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="md:col-span-7">
            <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-5">
              Notre approche
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1E1F1C] leading-[1.05]">
              Une approche dédiée
              <br />
              <em className="italic font-light">aux petites surfaces.</em>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-sans text-base md:text-lg text-[#5C5D58] leading-relaxed">
              Vendre un studio ou un 2 pièces à Paris ne s'improvise pas. Chaque
              mètre carré compte, chaque acquéreur a son profil. Nous avons fait
              de cette niche notre spécialité.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div
            className="md:col-span-5 md:row-span-2 relative min-h-[420px] md:min-h-[600px] overflow-hidden"
            data-testid="trust-feature-card"
          >
            <img
              src={IMG.brickTexture}
              alt="Détail de briques rouges parisiennes"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E1F1C] via-[#1E1F1C]/50 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-10">
              <p className="text-xs font-sans uppercase tracking-[0.25em] font-medium text-[#D48D6A] mb-4">
                Notre terrain
              </p>
              <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                Paris intra-muros & proche couronne
              </h3>
              <p className="font-sans text-sm md:text-base text-white/80 leading-relaxed mt-4 max-w-md">
                9ᵉ, 11ᵉ, 18ᵉ, 20ᵉ, Boulogne, Clichy, Vincennes... Une connaissance du marché développée secteur par secteur.
              </p>
            </div>
          </div>

          {benefits.slice(0, 2).map((b) => (
            <div
              key={b.n}
              className="md:col-span-7 bg-white border border-[#E5E0D8] p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300"
              data-testid={`benefit-card-${b.n}`}
            >
              <div className="flex items-start gap-6 md:gap-8">
                <span className="font-serif text-3xl md:text-4xl text-[#B85C38] mt-1">
                  {b.n}
                </span>
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#1E1F1C] leading-tight">
                    {b.t}
                  </h3>
                  <p className="font-sans text-base text-[#5C5D58] leading-relaxed mt-3">
                    {b.d}
                  </p>
                </div>
              </div>
            </div>
          ))}

          <div
            className="md:col-span-12 bg-white border border-[#E5E0D8] p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300"
            data-testid={`benefit-card-${benefits[2].n}`}
          >
            <div className="flex items-start gap-6 md:gap-8">
              <span className="font-serif text-3xl md:text-4xl text-[#B85C38] mt-1">
                {benefits[2].n}
              </span>
              <div className="max-w-3xl">
                <h3 className="font-serif text-2xl md:text-3xl text-[#1E1F1C] leading-tight">
                  {benefits[2].t}
                </h3>
                <p className="font-sans text-base text-[#5C5D58] leading-relaxed mt-3">
                  {benefits[2].d}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- ABOUT (À propos) — short version ----------
const About = () => (
  <section
    id="a-propos"
    data-testid="about-section"
    className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#F9F8F6]"
  >
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
      <div className="md:col-span-5">
        <div className="img-frame aspect-[4/5] w-full">
          <img
            src={IMG.founder}
            alt="Pierre Travers, L'Expert Parisien"
            className="w-full h-full object-cover"
            data-testid="founder-image"
          />
        </div>
        <div className="mt-6 border-l-2 border-[#B85C38] pl-5">
          <div className="font-sans text-xs uppercase tracking-[0.25em] text-[#B85C38]">
            L'Expert Parisien · Fondateur
          </div>
          <div className="font-serif text-2xl text-[#1E1F1C] mt-2 leading-tight">
            Pierre Travers
            <br />
            <em className="italic font-light text-lg">
              en partenariat avec{" "}
              <a
                href={CONTACT.partnerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-founder-flatlink-link"
                className="not-italic underline decoration-[#B85C38] underline-offset-4 hover:text-[#B85C38] transition-colors"
              >
                Flatlink
              </a>
              .
            </em>
          </div>

          <a
            href={CONTACT.partnerReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-google-reviews"
            className="mt-5 inline-flex items-center gap-2 group"
          >
            <span className="text-[#B85C38] tracking-[0.1em] text-sm" aria-hidden="true">
              ★★★★★
            </span>
            <span className="font-sans text-sm text-[#1E1F1C] group-hover:text-[#B85C38] transition-colors">
              <span className="font-medium">4,9/5</span>
              <span className="text-[#5C5D58]"> sur Google </span>
              <span className="text-[#5C5D58]">(31 avis)</span>
            </span>
          </a>

          <a
            href={CONTACT.partnerReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-google-reviews-button"
            className="mt-4 inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] text-[#1E1F1C] border-b border-[#1E1F1C] pb-1 hover:text-[#B85C38] hover:border-[#B85C38] transition-colors"
          >
            Consulter les avis Google
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
        </div>
      </div>

      <div className="md:col-span-7">
        <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-5">
          À propos
        </p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-[56px] font-medium tracking-tight text-[#1E1F1C] leading-[1.05]">
          Un accompagnement
          <br />
          dédié aux <em className="italic font-light">petites surfaces.</em>
        </h2>

        <div className="mt-8 space-y-5 max-w-2xl">
          <p className="font-sans text-base md:text-lg text-[#5C5D58] leading-relaxed">
            Studios, 1 et 2 pièces à Paris et en proche couronne :{" "}
            <span className="text-[#1E1F1C] font-medium">
              un seul marché, une expertise affûtée.
            </span>{" "}
            Connaissance précise des prix au m², des immeubles et des profils d'acquéreurs propres à chaque secteur.
          </p>
          <p className="font-sans text-base md:text-lg text-[#5C5D58] leading-relaxed">
            Stratégie de prix optimisée pour maximiser le net vendeur, mise en
            relation directe avec un{" "}
            <span className="text-[#1E1F1C] font-medium">
              vivier d'investisseurs et primo-accédants qualifiés
            </span>
            , commercialisation réalisée avec le support de{" "}
            <a
              href={CONTACT.partnerUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-flatlink-link"
              className="text-[#1E1F1C] font-medium underline decoration-[#B85C38] underline-offset-4 hover:text-[#B85C38] transition-colors"
            >
              Flatlink
            </a>
            .
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-3 font-sans text-sm md:text-base text-[#1E1F1C] border-t border-[#E5E0D8] pt-7">
          <span className="font-medium">Interlocuteur unique</span>
          <span className="text-[#B85C38]">•</span>
          <span className="font-medium">Stratégie sur-mesure</span>
          <span className="text-[#B85C38]">•</span>
          <span className="font-medium">Résultats concrets</span>
        </div>

        <div className="mt-10">
          <a
            href="#contact"
            data-testid="about-cta"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#1E1F1C] text-white font-sans text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#B85C38] transition-colors"
          >
            Discutons de votre projet
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ---------- HONORAIRES ----------
const Honoraires = () => {
  const included = [
    "Estimation offerte",
    "Stratégie de vente adaptée au bien",
    "Pack photos premium offert",
    "Diffusion auprès du club Flatlink (+500 investisseurs actifs)",
    "Diffusion sur les principaux portails (dont SeLoger)",
    "Accompagnement complet jusqu'à la signature",
  ];

  return (
    <section
      id="honoraires"
      data-testid="honoraires-section"
      className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 md:mb-16 items-end">
          <div className="md:col-span-7">
            <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-5">
              Honoraires
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1E1F1C] leading-[1.05]">
              Une grille simple,
              <br />
              <em className="italic font-light">transparente.</em>
            </h2>
          </div>
          <p className="md:col-span-5 font-sans text-base md:text-lg text-[#5C5D58] leading-relaxed">
            Honoraires réduits, services premium inclus. Aucune surprise, tout
            est annoncé dès la première estimation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div
            className="bg-white border border-[#E5E0D8] p-8 md:p-10 flex flex-col"
            data-testid="honoraire-tier-1"
          >
            <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#5C5D58]">
              Prix de vente
            </div>
            <div className="font-serif text-2xl md:text-3xl text-[#1E1F1C] mt-1">
              Jusqu'à 200 000 €
            </div>
            <div className="mt-8 pt-8 border-t border-[#E5E0D8]">
              <div className="font-serif text-5xl md:text-6xl text-[#B85C38] leading-none">
                7 000 €
              </div>
              <div className="font-sans text-sm text-[#5C5D58] mt-2 tracking-wide">
                TTC · forfait
              </div>
            </div>
          </div>

          <div
            className="bg-[#1E1F1C] text-white p-8 md:p-10 flex flex-col"
            data-testid="honoraire-tier-2"
          >
            <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#D48D6A]">
              Prix de vente
            </div>
            <div className="font-serif text-2xl md:text-3xl mt-1">
              À partir de 200 000 €
            </div>
            <div className="mt-8 pt-8 border-t border-white/15">
              <div className="font-serif text-5xl md:text-6xl text-[#D48D6A] leading-none">
                3,5 %
              </div>
              <div className="font-sans text-sm text-white/70 mt-2 tracking-wide">
                TTC du prix de vente
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 bg-[#F0EBE1] border border-[#E5E0D8] p-8 md:p-12">
          <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#B85C38] mb-6">
            Inclus dans tous les forfaits
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {included.map((s, i) => (
              <li
                key={i}
                data-testid={`honoraire-include-${i}`}
                className="flex items-start gap-4 border-b border-[#E5E0D8] pb-4 last:border-b-0 md:last:border-b md:[&:nth-last-child(-n+2)]:border-b-0"
              >
                <span className="font-serif text-sm text-[#B85C38] mt-1 shrink-0">
                  0{i + 1}
                </span>
                <span className="font-sans text-base text-[#1E1F1C] leading-snug">
                  {s}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-6 border-t border-[#E5E0D8]">
            <p className="font-sans text-sm text-[#5C5D58] max-w-md">
              Diffusion réalisée avec le support de{" "}
              <a
                href={CONTACT.partnerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1E1F1C] underline decoration-[#B85C38] underline-offset-4 hover:text-[#B85C38] transition-colors"
              >
                Flatlink
              </a>
              , agence partenaire notée 4,9/5 sur Google.
            </p>
            <a
              href="#contact"
              data-testid="honoraire-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#B85C38] text-white font-sans text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#9A4C2E] transition-colors"
            >
              Estimer mon bien
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- RECENT SALES ----------
const Sales = () => {
  const cases = [
    {
      img: IMG.case1,
      type: "Studio",
      loc: "Paris 11ᵉ",
      result: "Vendu en 9 jours",
      detail: "Au prix demandé · 2 visites",
      surface: "21 m²",
    },
    {
      img: IMG.case2,
      type: "2 pièces",
      loc: "Boulogne-Billancourt",
      result: "Prix optimal atteint",
      detail: "+4 % au-dessus de l'estimation initiale",
      surface: "38 m²",
    },
    {
      img: IMG.case3,
      type: "Studio",
      loc: "Paris 18ᵉ",
      result: "Vendu sans négociation",
      detail: "Acquéreur ciblé · dossier solide",
      surface: "18 m²",
    },
  ];

  return (
    <section
      id="realisations"
      data-testid="sales-section"
      className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#F0EBE1]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div>
            <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-5">
              Réalisations récentes
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1E1F1C] leading-[1.05] max-w-3xl">
              Des ventes réelles,
              <br />
              <em className="italic font-light">des résultats concrets.</em>
            </h2>
          </div>
          <p className="font-sans text-base md:text-lg text-[#5C5D58] max-w-md leading-relaxed">
            Quelques exemples récents de biens vendus pour des propriétaires en
            Île-de-France.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cases.map((c, i) => (
            <article
              key={i}
              className="group bg-white border border-[#E5E0D8] flex flex-col"
              data-testid={`case-card-${i}`}
            >
              <div className="img-frame aspect-[4/5] overflow-hidden">
                <img
                  src={c.img}
                  alt={`${c.type} ${c.loc}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-7 md:p-8 flex flex-col flex-1">
                <div className="flex items-baseline justify-between border-b border-[#E5E0D8] pb-4">
                  <h3 className="font-serif text-2xl md:text-[26px] text-[#1E1F1C]">
                    {c.type} — {c.loc}
                  </h3>
                  <span className="font-sans text-xs text-[#5C5D58] whitespace-nowrap">
                    {c.surface}
                  </span>
                </div>
                <p className="font-serif text-lg italic text-[#B85C38] mt-5">
                  {c.result}
                </p>
                <p className="font-sans text-sm text-[#5C5D58] mt-2 leading-relaxed">
                  {c.detail}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- TESTIMONIAL ----------
const Testimonial = () => (
  <section
    data-testid="testimonial-section"
    className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#1E1F1C]"
  >
    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
      <div className="md:col-span-4 order-2 md:order-1">
        <div className="img-frame aspect-[3/4] max-w-[280px]">
          <img
            src={IMG.testimonial}
            alt="Grégoire, propriétaire vendeur"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="mt-5">
          <div className="font-serif text-xl text-white">Grégoire</div>
          <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#D48D6A] mt-1">
            2 pièces · Paris 14ᵉ
          </div>
        </div>
      </div>

      <div className="md:col-span-8 order-1 md:order-2">
        <Quote className="w-12 h-12 text-[#B85C38] mb-6" strokeWidth={1} />
        <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-[1.2] tracking-tight">
          « Mon appartement a été vendu en moins d'un mois, au prix.
          <br />
          <em className="italic font-light text-[#D48D6A]">
            Un accompagnement très efficace,
          </em>
          {" "}sérieux et discret. »
        </blockquote>
      </div>
    </div>
  </section>
);

// ---------- PROCESS ----------
const Process = () => {
  const steps = [
    {
      n: "01",
      t: "Évaluation du bien",
      d: "Visite, analyse comparative, étude du marché local. Vous recevez une estimation précise et argumentée, sous 48h.",
    },
    {
      n: "02",
      t: "Marketing ciblé",
      d: "Mise en valeur du bien, photos professionnelles, diffusion auprès de notre réseau d'acquéreurs qualifiés.",
    },
    {
      n: "03",
      t: "Vente finalisée",
      d: "Sélection des meilleurs profils, négociation et accompagnement jusqu'à la signature chez le notaire.",
    },
  ];

  return (
    <section
      id="processus"
      data-testid="process-section"
      className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-5">
            Comment ça se passe
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1E1F1C] leading-[1.05]">
            Un processus clair,
            <br />
            <em className="italic font-light">en trois étapes.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="relative pt-12 border-t border-[#1E1F1C]"
              data-testid={`process-step-${s.n}`}
            >
              <span className="absolute -top-2 left-0 font-serif text-[120px] md:text-[160px] leading-none text-[#E5E0D8] -z-0 select-none">
                {s.n}
              </span>
              <div className="relative z-10">
                <div className="font-sans text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-3">
                  Étape {i + 1}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1E1F1C] leading-tight">
                  {s.t}
                </h3>
                <p className="font-sans text-base text-[#5C5D58] leading-relaxed mt-4">
                  {s.d}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- FINAL CTA + CONTACT ----------
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Merci de renseigner votre nom et votre email.");
      return;
    }
    const subject = encodeURIComponent(
      `Demande d'estimation — ${form.name}`
    );
    const body = encodeURIComponent(
      `Bonjour,\n\nJe souhaite obtenir une estimation de mon bien.\n\nNom : ${form.name}\nEmail : ${form.email}\nTéléphone : ${form.phone}\nAdresse / arrondissement : ${form.address}\n\nMessage :\n${form.message}\n\nCordialement,\n${form.name}`
    );
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
    toast.success("Votre client mail s'ouvre... ");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 px-6 md:px-10 lg:px-12 bg-[#F0EBE1]"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <p className="text-xs md:text-sm font-sans uppercase tracking-[0.25em] font-medium text-[#B85C38] mb-5">
            Prendre contact
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1E1F1C] leading-[1.05]">
            Obtenez une estimation
            <br />
            <em className="italic font-light">fiable de votre bien.</em>
          </h2>
          <p className="font-sans text-base md:text-lg text-[#5C5D58] leading-relaxed mt-6 max-w-md">
            Réponse personnalisée sous 48 heures. Confidentiel, sans engagement.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="w-10 h-10 border border-[#1E1F1C] flex items-center justify-center group-hover:bg-[#B85C38] group-hover:border-[#B85C38] group-hover:text-white transition-colors">
                <Phone className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                  Téléphone
                </div>
                <div className="font-serif text-xl text-[#1E1F1C]">
                  {CONTACT.phone}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <span className="w-10 h-10 border border-[#1E1F1C] flex items-center justify-center group-hover:bg-[#B85C38] group-hover:border-[#B85C38] group-hover:text-white transition-colors">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                  Email
                </div>
                <div className="font-serif text-xl text-[#1E1F1C]">
                  {CONTACT.email}
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <span className="w-10 h-10 border border-[#1E1F1C] flex items-center justify-center">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                  Zone d'intervention
                </div>
                <div className="font-serif text-xl text-[#1E1F1C]">
                  {CONTACT.address}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-white border border-[#E5E0D8] p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7"
          >
            <div className="md:col-span-1">
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                Nom complet
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                data-testid="form-name"
                placeholder="Votre nom"
                className="w-full bg-transparent border-b border-[#E5E0D8] py-3 mt-2 text-[#1E1F1C] font-sans placeholder:text-[#9A9B96] focus:outline-none focus:border-[#B85C38] transition-colors"
              />
            </div>
            <div className="md:col-span-1">
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                Email
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                data-testid="form-email"
                placeholder="vous@email.com"
                className="w-full bg-transparent border-b border-[#E5E0D8] py-3 mt-2 text-[#1E1F1C] font-sans placeholder:text-[#9A9B96] focus:outline-none focus:border-[#B85C38] transition-colors"
              />
            </div>
            <div className="md:col-span-1">
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                Téléphone
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                data-testid="form-phone"
                placeholder="06 XX XX XX XX"
                className="w-full bg-transparent border-b border-[#E5E0D8] py-3 mt-2 text-[#1E1F1C] font-sans placeholder:text-[#9A9B96] focus:outline-none focus:border-[#B85C38] transition-colors"
              />
            </div>
            <div className="md:col-span-1">
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                Adresse du bien
              </label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                data-testid="form-address"
                placeholder="Ex : 34 rue Marcadet, 75018 Paris"
                className="w-full bg-transparent border-b border-[#E5E0D8] py-3 mt-2 text-[#1E1F1C] font-sans placeholder:text-[#9A9B96] focus:outline-none focus:border-[#B85C38] transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5D58]">
                Votre projet
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="form-message"
                rows={4}
                placeholder="Surface, étage, état, particularités du bien..."
                className="w-full bg-transparent border-b border-[#E5E0D8] py-3 mt-2 text-[#1E1F1C] font-sans placeholder:text-[#9A9B96] focus:outline-none focus:border-[#B85C38] transition-colors resize-none"
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 mt-2">
              <p className="font-sans text-xs text-[#5C5D58] max-w-xs">
                Vos informations sont traitées en toute confidentialité.
              </p>
              <button
                type="submit"
                data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#B85C38] text-white font-sans text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#9A4C2E] transition-colors"
              >
                Demander mon estimation
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// ---------- FOOTER ----------
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-[#1E1F1C] text-white px-6 md:px-10 lg:px-12 pt-16 pb-10"
  >
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-white/10">
        <div className="md:col-span-5">
          <div className="font-serif text-2xl">{CONTACT.brand}</div>
          <div className="font-sans text-xs text-white/60 mt-2 tracking-wide">
            Spécialiste des petites surfaces — Paris & proche couronne
          </div>
          <div className="font-sans text-xs text-white/50 mt-4 leading-relaxed max-w-sm">
            En partenariat avec{" "}
            <a
              href={CONTACT.partnerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 underline decoration-[#B85C38] underline-offset-4 hover:text-[#D48D6A] transition-colors"
            >
              Flatlink
            </a>
            , agence immobilière partenaire notée 4,9/5 sur Google.
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#D48D6A] mb-4">
            Contact
          </div>
          <ul className="space-y-2 font-sans text-sm text-white/80">
            <li>Pierre Travers</li>
            <li>
              <a
                href={`mailto:${CONTACT.email}`}
                className="hover:text-[#D48D6A] transition-colors"
              >
                {CONTACT.email}
              </a>
            </li>
            <li>{CONTACT.phone}</li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#D48D6A] mb-4">
            Mentions légales
          </div>
          <p className="font-sans text-xs text-white/50 leading-relaxed">
            {CONTACT.brand} — {CONTACT.domain}. Site édité par Pierre Travers.
            Commercialisation réalisée avec le support de{" "}
            <a
              href={CONTACT.partnerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-[#D48D6A] transition-colors"
            >
              Flatlink
            </a>
            . Carte professionnelle conforme à la loi Hoguet.
          </p>
        </div>
      </div>

      <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-sans text-xs text-white/40 tracking-wide">
        <div>
          © {new Date().getFullYear()} {CONTACT.brand}. Tous droits réservés.
        </div>
        <div>
          <a
            href={CONTACT.partnerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#D48D6A] transition-colors"
          >
            www.flatlink.fr
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// ---------- PAGE ----------
const LandingPage = () => {
  return (
    <main className="bg-[#F9F8F6]" data-testid="landing-page">
      <Header />
      <Hero />
      <Trust />
      <About />
      <Sales />
      <Testimonial />
      <Process />
      <Honoraires />
      <Contact />
      <Footer />
    </main>
  );
};

export default LandingPage;
